import { useRef, useMemo, useCallback } from 'react';
import { useCustomCompareMemo } from 'use-custom-compare';

// This hook takes in an object to be set as a context value.
// The values are stored in a ref so it always returns the same object.
const useDynamicRef = (object = {}) => {
  const dynamicRef = useRef({});
  const objKeys = useMemo(() => Object.keys(object), []);

  // updateDynamicRef takes in the keys and the object itself as paramters
  // without dependancies because contextAction is always reachable.
  const updateDynamicRef = useCallback((keys, obj) => {
    for (let i = 0; i < keys.length; i += 1) {
      dynamicRef.current[keys[i]] = obj[keys[i]];
    }
  }, []);

  // run useMemo to call updateDynamicRef prior to first useEffect
  // this ensures that dynamicRef has properties on first render.
  useMemo(() => {
    updateDynamicRef(objKeys, object);
  }, []);

  useCustomCompareMemo(() => {
    updateDynamicRef(objKeys, object);
  }, [object], (prevDeps, nextDeps) => {
    // prevDeps is always [] on first run .. return false to run initial callback
    if (prevDeps.length === 0) {
      return false;
    }
    // loop through the object to check if both variables are the same or point to the same reference
    // if not return false to run callback
    for (let i = 0; i < objKeys.length; i += 1) {
      if (prevDeps[0][objKeys[i]] !== nextDeps[0][objKeys[i]]) {
        return false;
      }
    }
    // if it get's here, all properties matched, no need to run callback.
    return true;
  });

  return dynamicRef.current;
};

export default useDynamicRef;
